import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core";
import {
  TABLE_FONT,
  CELL_FG_STRONG,
  CELL_FG_MUTED,
  TABLE_HAIRLINE,
  TH_BG,
  TH_BG_BAND,
} from "../../reportTableConfig";

export const PAGE_SIZE_OPTIONS = [10, 50, 100, 250, 500];

/** Same page-size/slice/clamp math as PL-by-Program's PSRTrend pagination. */
export function usePagination(items, initialPageSize = PAGE_SIZE_OPTIONS[0]) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedItems = useMemo(
    () => items.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [items, safePage, pageSize],
  );

  const goToPage = (p) => setPage(Math.max(0, Math.min(p, totalPages - 1)));
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(0);
  };

  return { page: safePage, pageSize, totalPages, pagedItems, goToPage, handlePageSizeChange, resetPage: () => setPage(0) };
}

const useStyles = makeStyles(() => ({
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 20px", borderTop: `1px solid ${TABLE_HAIRLINE}`,
    background: "#fff", flexWrap: "wrap", gap: 10,
  },
  paginationInfo: {
    display: "flex", alignItems: "center", gap: 8,
    fontSize: 13, color: CELL_FG_MUTED, fontFamily: TABLE_FONT,
  },
  pageSizeLabel: { fontSize: 13, color: CELL_FG_MUTED, fontFamily: TABLE_FONT, whiteSpace: "nowrap" },
  pageSizeSelectWrap: { position: "relative", display: "inline-flex", alignItems: "center" },
  pageSizeSelect: {
    appearance: "none", WebkitAppearance: "none",
    height: 32, padding: "0 28px 0 10px", borderRadius: 7,
    border: "1px solid #D1D5DB", background: "#fff",
    fontSize: 13, fontWeight: 500, color: CELL_FG_STRONG, fontFamily: TABLE_FONT,
    cursor: "pointer", outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: "rgb(51, 65, 85)" },
  },
  pageSizeChevron: {
    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
    pointerEvents: "none", color: "rgb(51, 65, 85)",
  },
  paginationControls: { display: "flex", alignItems: "center", gap: 3 },
  pageBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: 32, height: 32, padding: "0 6px", borderRadius: 7,
    border: "1px solid #E5E7EB", background: "#fff",
    fontSize: 13, fontWeight: 500, color: CELL_FG_STRONG,
    cursor: "pointer", fontFamily: TABLE_FONT, lineHeight: 1,
    transition: "background 0.12s, border-color 0.12s, color 0.12s",
    "&:hover:not(:disabled)": { background: "#F3F4F6", borderColor: "#D1D5DB" },
    "&:disabled": { opacity: 0.35, cursor: "default" },
  },
  pageBtnActive: {
    background: TH_BG, borderColor: TH_BG, color: "#fff",
    fontWeight: 600,
    "&:hover:not(:disabled)": { background: TH_BG_BAND, borderColor: TH_BG_BAND, color: "#fff" },
  },
  pageEllipsis: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: 32, height: 32, fontSize: 13, color: CELL_FG_MUTED, fontFamily: TABLE_FONT,
    letterSpacing: "0.05em",
  },
}));

/** Numbered page buttons with ellipsis gaps — identical logic to PSRTrend's PageButtons. */
function PageButtons({ page, totalPages, onPageChange, classes }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 1;
  const ranges = new Set([0, totalPages - 1]);
  for (let i = Math.max(0, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
    ranges.add(i);
  }
  const sorted = [...ranges].sort((a, b) => a - b);

  let prev = -1;
  sorted.forEach((p) => {
    if (prev !== -1 && p - prev > 1) pages.push("…");
    pages.push(p);
    prev = p;
  });

  return pages.map((p, i) =>
    p === "…" ? (
      <span key={`ellipsis-${i}`} className={classes.pageEllipsis}>…</span>
    ) : (
      <button
        key={p}
        type="button"
        className={`${classes.pageBtn} ${p === page ? classes.pageBtnActive : ""}`}
        onClick={() => onPageChange(p)}
      >
        {p + 1}
      </button>
    ),
  );
}

/** Rows-per-page selector + first/prev/numbered/next/last controls — matches PL-by-Program's pagination bar. */
export default function PaginationBar({ page, pageSize, totalPages, totalItems, onPageChange, onPageSizeChange }) {
  const classes = useStyles();
  const rangeStart = totalItems === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = Math.min((page + 1) * pageSize, totalItems);

  return (
    <div className={classes.pagination}>
      <div className={classes.paginationInfo}>
        <span className={classes.pageSizeLabel}>Rows Per Page</span>
        <div className={classes.pageSizeSelectWrap}>
          <select className={classes.pageSizeSelect} value={pageSize} onChange={onPageSizeChange}>
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className={classes.pageSizeChevron}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 4.5L6 8l3.5-3.5" stroke="rgb(51, 65, 85)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
        <span>
          {totalItems === 0 ? "0 rows" : `${rangeStart}–${rangeEnd} of ${totalItems.toLocaleString("en-US")}`}
        </span>
      </div>

      <div className={classes.paginationControls}>
        <button type="button" className={classes.pageBtn} onClick={() => onPageChange(0)} disabled={page === 0} title="First Page">«</button>
        <button type="button" className={classes.pageBtn} onClick={() => onPageChange(page - 1)} disabled={page === 0} title="Previous Page">‹</button>
        <PageButtons page={page} totalPages={totalPages} onPageChange={onPageChange} classes={classes} />
        <button type="button" className={classes.pageBtn} onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1} title="Next Page">›</button>
        <button type="button" className={classes.pageBtn} onClick={() => onPageChange(totalPages - 1)} disabled={page >= totalPages - 1} title="Last Page">»</button>
      </div>
    </div>
  );
}
